/* ------------------------------------------------------------------ */
/* Embedded Create-Connection forms (copied for identical UI)         */
/* ------------------------------------------------------------------ */

import {Button} from "react-bootstrap";
import {Input} from "@progress/kendo-react-inputs";
import {DropDownList} from "@progress/kendo-react-dropdowns";
import {saveSettings, testNewConnection} from "../../../actions/OnBoardingActions";
import {useEffect, useState} from "react";
import {semcastAxios} from "../../../Utils/Common";
import {IDENTITY_PIPELINE} from "../../../api/constants";
import $ from "jquery";

export const regions = [
    { text: "US East (N. Virginia)", id: "us-east-1" },
    { text: "US East (Ohio)", id: "us-east-2" },
    { text: "US West (N. California)", id: "us-west-1" },
    { text: "US West (Oregon)", id: "us-west-2" },
    { text: "Canada (Central)", id: "ca-central-1" },
    { text: "EU (Frankfurt)", id: "eu-central-1" },
    { text: "EU (Ireland)", id: "eu-west-1" },
    { text: "EU (London)", id: "eu-west-2" },
    { text: "EU (Paris)", id: "eu-west-3" },
    { text: "Asia Pacific (Mumbai)", id: "ap-south-1" },
    { text: "Asia Pacific (Tokyo)", id: "ap-northeast-1" },
    { text: "Asia Pacific (Seoul)", id: "ap-northeast-2" },
    { text: "China (Beijing)", id: "cn-north-1" },
    { text: "China (Ningxia)", id: "cn-northwest-1" },
    { text: "South America (Sao Paulo)", id: "sa-east-1" },
];

// Minimal, UI-identical S3 form
export function S3EmbeddedForm({ onSaved, onCancel, onBusyChange }) {
    const [selectedRegion, setSelectedRegion] = useState(regions[0]);
    const [form, setForm] = useState({
        connection_name: "",
        delimiter: "/",
        host: regions[0].id,
        user_name: "",
        password: "",
        starting_path: "",
        transfer_protocol: "s3",
    });
    const [testState, setTestState] = useState({ status: "idle", message: "" });
    const [isBusy, setIsBusy] = useState(false);
    const setBusy = (flag) => {
        setIsBusy(flag);
        onBusyChange?.(flag);
    };

    const set = (name) => (e) => setForm((p) => ({ ...p, [name]: e.value }));

    const hasValue = (value) => typeof value === "string" ? value.trim().length > 0 : !!value;
    const isFormValid = hasValue(form.connection_name) && hasValue(form.user_name) && hasValue(form.password);

    const createPayload = () => {
        let startingPath = form.starting_path || "";
        const delimiter = form.delimiter || "/";
        if (delimiter && startingPath.split(delimiter).length > 1 && !startingPath.endsWith(delimiter)) {
            startingPath += delimiter;
        }
        return {
            connection_name: form.connection_name,
            host: selectedRegion.id,
            delimiter,
            password: form.password,
            starting_path: startingPath,
            transfer_protocol: "s3",
            user_name: form.user_name,
        };
    };

    const testConn = async () => {
        if (!isFormValid) {
            setTestState({ status: "error", message: "Please fill in all required fields before testing." });
            return;
        }
        setBusy(true);
        setTestState({ status: "testing", message: "Testing connection…" });
        try {
            const payload = createPayload();
            await testNewConnection(payload, "s3", false);
            setTestState({ status: "success", message: "Connection is valid." });
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Connection failed.";
            setTestState({ status: "error", message: msg });
        } finally {
            setBusy(false);
        }
    };

    const saveConn = async () => {
        if (!isFormValid) {
            setTestState({ status: "error", message: "Please fill in all required fields before saving." });
            return;
        }
        setBusy(true);
        try {
            const payload = createPayload();
            await testNewConnection(payload, "s3", true);
            await saveSettings(payload, form.connection_name);
            onSaved?.(payload.connection_name);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="ip-section">
            <div className="ip-section-title">S3 Connection</div>

            <div className="ip-two-col">
                <label className="ip-field">
                    <span>Connection Name<span className="required-field">*</span></span>
                    <Input value={form.connection_name} onChange={set("connection_name")} className="text-field" />
                </label>

                <label className="ip-field">
                    <span>Region<span className="required-field">*</span></span>
                    <DropDownList
                        data={regions}
                        textField="text"
                        dataItemKey="id"
                        value={selectedRegion}
                        onChange={(e) => {
                            setSelectedRegion(e.value);
                            setForm((p) => ({ ...p, host: e.value.id }));
                        }}
                    />
                </label>
            </div>

            <div className="ip-one-col">
                <label className="ip-field">
                    <span>Starting Path</span>
                    <Input value={form.starting_path} onChange={set("starting_path")} className="text-field" />
                </label>
                <label className="ip-field">
                    <span>Delimiter<span className="required-field">*</span></span>
                    <Input value={form.delimiter} onChange={set("delimiter")} className="text-field" />
                </label>

            </div>

            <div className="ip-two-col">
                <label className="ip-field">
                    <span>Access Key<span className="required-field">*</span></span>
                    <Input
                        value={form.user_name}
                        onChange={set("user_name")}
                        className="text-field"
                        autoComplete="off"
                    />
                </label>
                <label className="ip-field">
                    <span>Secret Key<span className="required-field">*</span></span>
                    <Input
                        type="password"
                        value={form.password}
                        onChange={set("password")}
                        className="text-field"
                        autoComplete="new-password"
                    />
                </label>
            </div>

            <div className="ip-test-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 12 }} />
                <div className="ip-conn-actions">
                    <Button
                        className="button-standard button-white"
                        onClick={testConn}
                        disabled={isBusy || !isFormValid}
                        style={{width: "auto", minWidth: 150, fontSize: 14}}
                    >
                        Test Connection
                    </Button>
                    <Button
                        className="button-standard button-submit"
                        onClick={saveConn}
                        disabled={isBusy || !isFormValid}
                        style={{fontSize: 14}}
                    >
                        Save
                    </Button>
                    <Button className="button-standard" onClick={onCancel} style={{fontSize: 14}}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Minimal, UI-identical SFTP/FTPS form
export function FTPEmbeddedForm({ onSaved, onCancel, onBusyChange }) {
    const transferModes = [
        { text: "Passive", id: true },
        { text: "Active", id: false },
    ];
    const [ftpProtocol, setFtpProtocol] = useState("sftp");
    const [transferModeValue, setTransferModeValue] = useState(transferModes[0]);
    const [form, setForm] = useState({
        connection_name: "",
        host: "",
        passive_transfer_mode: true,
        user_name: "",
        password: "",
        port: "",
        starting_path: "",
        transfer_protocol: "ftps",
    });
    const [testState, setTestState] = useState({ status: "idle", message: "" });
    const [isBusy, setIsBusy] = useState(false);
    const setBusy = (flag) => {
        setIsBusy(flag);
        onBusyChange?.(flag);
    };

    const set = (name) => (e) => setForm((p) => ({ ...p, [name]: e.value }));

    const getHostName = (link) => {
        try { return new URL(link).host; } catch { return link || ""; }
    };

    const hasValue = (value) => typeof value === "string" ? value.trim().length > 0 : !!value;
    const isFormValid =
        hasValue(form.connection_name) &&
        hasValue(form.host) &&
        hasValue(form.user_name) &&
        hasValue(form.password);

    const createPayload = () => {
        const hostName = getHostName(form.host);
        return {
            connection_name: form.connection_name,
            host: hostName,
            passive_transfer_mode: transferModeValue.id,
            password: form.password,
            port: form.port,
            starting_path: form.starting_path,
            transfer_protocol: ftpProtocol,
            user_name: form.user_name,
        };
    };

    const testConn = async () => {
        if (!isFormValid) {
            setTestState({ status: "error", message: "Please fill in all required fields before testing." });
            return;
        }
        setBusy(true);
        setTestState({ status: "testing", message: "Testing connection…" });
        try {
            const payload = createPayload();
            await testNewConnection(payload, "ftp", false);
            setTestState({ status: "success", message: "Connection is valid." });
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Connection failed.";
            setTestState({ status: "error", message: msg });
        } finally {
            setBusy(false);
        }
    };

    const saveConn = async () => {
        if (!isFormValid) {
            setTestState({ status: "error", message: "Please fill in all required fields before saving." });
            return;
        }
        setBusy(true);
        try {
            const payload = createPayload();
            await testNewConnection(payload, "ftp", true);
            await saveSettings(payload, form.connection_name);
            onSaved?.(payload.connection_name);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="ip-section">
            <div className="ip-section-title">FTP/SFTP Connection</div>

            <div className="ip-two-col">
                <label className="ip-field">
                    <span>Connection Name<span className="required-field">*</span></span>
                    <Input value={form.connection_name} onChange={set("connection_name")} className="text-field" />
                </label>

            </div>
<div className="ip-col">
    <div className="ip-field ip-field--inline mt-2">
        <span>FTP Host Site<span className="required-field">*</span></span>
        <div className="connection-inline-row">
            <DropDownList
                data={[
                    { id: "sftp", text: "SFTP" },
                    { id: "ftps", text: "FTPS" },
                ]}
                textField="text"
                dataItemKey="id"
                value={{ id: ftpProtocol, text: ftpProtocol?.toUpperCase() }}
                onChange={(e) => setFtpProtocol(e.value.id)}
                className="dropdown-standard"
                style={{ minWidth: 110 }}
            />
            <div className="ip-host-field">
                <span>URL:</span>
                <Input value={form.host} onChange={set("host")} className="text-field" />
            </div>
            <Input value={form.port} onChange={set("port")} className="text-field" placeholder="Port" />
        </div>
    </div>
</div>
            <div className="ip-one-col">
                <label className="ip-field">
                    <span>Destination Directory</span>
                    <Input value={form.starting_path} onChange={set("starting_path")} className="text-field" />
                </label>

                <label className="ip-field">
                    <span>Transfer Mode</span>
                    <DropDownList
                        data={transferModes}
                        dataItemKey="id"
                        textField="text"
                        value={transferModeValue}
                        onChange={(e) => {
                            setTransferModeValue(e.value);
                            setForm((p) => ({ ...p, passive_transfer_mode: e.value.id }));
                        }}
                        className="dropdown-standard"
                        title="Try changing this if you can't connect"
                        style={{ minWidth: 110 }}
                    />
                </label>
            </div>

            <div className="ip-two-col">
                <label className="ip-field">
                    <span>User Name<span className="required-field">*</span></span>
                    <Input
                        value={form.user_name}
                        onChange={set("user_name")}
                        className="text-field"
                        autoComplete="off"
                    />
                </label>

                <label className="ip-field">
                    <span>Password<span className="required-field">*</span></span>
                    <Input
                        type="password"
                        value={form.password}
                        onChange={set("password")}
                        className="text-field"
                        autoComplete="new-password"
                    />
                </label>
            </div>

            <div className="ip-test-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 12 }} />
                <div className="ip-conn-actions">
                    <Button
                        className="button-standard button-white"
                        onClick={testConn}
                        disabled={isBusy || !isFormValid}
                        style={{width: "auto", minWidth: 150, fontSize: 14}}
                    >
                        Test Connection
                    </Button>
                    <Button
                        className="button-standard button-submit"
                        onClick={saveConn}
                        disabled={isBusy || !isFormValid}
                        style={{fontSize: 14}}
                    >
                        Save
                    </Button>
                    <Button className="button-standard" onClick={onCancel} style={{fontSize: 14}}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
}



export const CREATE_ITEM = { id: "__create__", name: "➕ Create new connection…" };

export const INITIAL_FIELD_ERRORS = {
    name: false,
    inputConn: false,
    outputConn: false,
    inputFile: false,
    weeklyDay: false,
};

export const SCHEDULE_TYPE_ALL = [
    { id: "DAILY", name: "Daily" },
    { id: "WEEKLY", name: "Weekly" },
    { id: "MONTHLY", name: "Monthly" },
    { id: "UNSCHEDULED", name: "Unscheduled" },
];

export const dayOfWeekOptions = [
    { id: 0, name: "Sunday" },
    { id: 1, name: "Monday" },
    { id: 2, name: "Tuesday" },
    { id: 3, name: "Wednesday" },
    { id: 4, name: "Thursday" },
    { id: 5, name: "Friday" },
    { id: 6, name: "Saturday" },
];

export const dateOptions = Array.from({ length: 28 }, (_, i) => ({ id: i + 1, name: String(i + 1) }));

export const DEFAULT_GROUP_ITEM = { group_name: "Please Select...", id: "-1" };

export const COMPRESSION_OPTIONS = [
    { text: "BZIP2", value: "BZIP2" },
    { text: "GZIP", value: "GZIP" },
    { text: "LZOP", value: "LZOP" },
    { text: "ZSTD", value: "ZSTD" },
    { text: "NONE", value: "NONE" },
];
export const COMPRESSION_DEFAULT_ITEM = { text: "Please select…", value: null };

export const inferCompressionFromPath = (path = "") => {
    const normalized = (path || "").toLowerCase();
    if (!normalized) return null;
    if (normalized.endsWith(".bz2") || normalized.endsWith(".bz")) return "BZIP2";
    if (normalized.endsWith(".gz")) return "GZIP";
    if (normalized.endsWith(".lzop") || normalized.endsWith(".lzo")) return "LZOP";
    if (normalized.endsWith(".zst") || normalized.endsWith(".zstd")) return "ZSTD";
    return "NONE";
};

export const CONNECTION_TYPE_OPTIONS = [
    { id: "S3", name: "S3" },
    { id: "S3C", name: "S3 Compatible" },
    { id: "FTP", name: "FTPS/SFTP" },
];


// Same normalization used in the page
export function normalizeConnections(list = []) {
    const safeDelim = (raw) =>
        typeof raw.delimiter === "string" && raw.delimiter.length === 1 ? raw.delimiter : "/";

    return list
        .map((raw, i) => {
            const id =
                raw.id ??
                raw.connection_id ??
                raw.connId ??
                raw.uuid ??
                `conn_${Date.now()}_${i}`;

            const proto = (raw.transfer_protocol || raw.type || raw.protocol || "")
                .toString()
                .trim()
                .toUpperCase();

            let type;
            if (proto === "S3") type = "S3";
            else if (proto === "S3C") type = "S3C";
            else if (proto === "SFTP") type = "SFTP";
            else if (proto === "FTPS" || proto === "FTPES" || proto === "FTP_TLS") type = "FTPS";
            else if (proto === "FTP") type = undefined;

            if (!type) {
                if ((raw.transfer_protocol || "").toString().toLowerCase() === "s3") {
                    type = "S3";
                } else if ((raw.transfer_protocol || "").toString().toLowerCase() === "s3c") {
                    type = "S3C";
                } else if (raw.host && Number(raw.port) === 22) {
                    type = "SFTP";
                } else if (raw.host && (Number(raw.port) === 21 || Number(raw.port) === 990 || raw.passive_transfer_mode != null)) {
                    type = "FTPS";
                }
            }
            if (!["S3", "S3C", "SFTP", "FTPS"].includes(type)) return null;

            let name;
            if (type === "S3" || type === "S3C") {
                const region = raw.host || "";
                const delim = safeDelim(raw);
                const path = (raw.starting_path || "").replace(/^\/+/, "");
                const [bucket, ...rest] = path.split(delim);
                const prefix = rest.length ? rest.join(delim) : "";
                const label = type === "S3C" ? "S3 Compatible" : "S3";
                name = raw.connection_name || raw.name || `${label} - ${bucket || path || region || id}${prefix ? `/${prefix}` : ""}`;
            } else if (type === "SFTP") {
                const port = Number(raw.port) || 22;
                name = raw.connection_name || raw.name || `SFTP - ${raw.host || "host"}${raw.starting_path ? ` ${raw.starting_path}` : ""}`;
            } else {
                const port = Number(raw.port) || 21;
                name = raw.connection_name || raw.name || `FTPS - ${raw.host || "host"}${raw.starting_path ? ` ${raw.starting_path}` : ""}`;
            }

            return { id: String(id), name, type, _raw: raw };
        })
        .filter(Boolean);
}


export const getById = (arr, id) => arr.find((x) => x.id === id) || null;
export  const scheduleLabel = (form) => {
    if (form.scheduleType === "DAILY") return "Daily";
    if (form.scheduleType === "UNSCHEDULED") return "Unscheduled";
    if (form.scheduleType === "WEEKLY") {
        const day = getById(dayOfWeekOptions, form.scheduleWeeklyDay)?.name ?? "—";
        return `Weekly (${day})`;
    }
    const date = form.scheduleMonthlyDate ?? "—";
    return `Monthly (day ${date})`;
};

export const scheduleConfirmText = (form) => {
    if (form.scheduleType === "DAILY") return "Runs every day";
    if (form.scheduleType === "WEEKLY") {
        const day = getById(dayOfWeekOptions, form.scheduleWeeklyDay)?.name;
        return day ? `Runs weekly on ${day}` : "";
    }
    if (form.scheduleType === "MONTHLY") {
        const date = form.scheduleMonthlyDate;
        return Number.isInteger(date) ? `Runs monthly on the ${ordinalSuffix(date)}` : "";
    }
    if (form.scheduleType === "UNSCHEDULED") return "This Pipeline will be executed once upon successful creation.";
    return "";
    return "";
};

export function ordinalSuffix(n) {
    if (typeof n !== "number") return "";
    const j = n % 10, k = n % 100;
    if (j === 1 && k !== 11) return n + "st";
    if (j === 2 && k !== 12) return n + "nd";
    if (j === 3 && k !== 13) return n + "rd";
    return n + "th";
}

export function renderDetectMapInsights(dm) {
    if (!dm) return null;

    // safe accessors
    const originalFilename = dm?.originalFilename ?? dm?.fileName ?? "";
    const delimiter = dm?.delimiter ?? dm?.incoming_delimiter ?? "";
    const headerDetected = dm?.headerDetected ?? dm?.hasHeader ?? undefined;
    const thoughts = dm?.thoughts ?? "";
    const sampleLines = Array.isArray(dm?.sampleLines) ? dm.sampleLines.slice(0, 5) : [];
    const supportedFileMessage = "Pipeline supports the following file types: Postal, Raw Email, Devices, IP/Timestamps."

    // NEW: mappingJson (headers -> type), with a few common fallbacks
    const mapping =
        dm?.mappingJSON || null;

    // turn mapping object into rows like [{key, type}]
    const mappingRows = mapping && typeof mapping === "object"
        ? Object.entries(mapping).map(([k, v]) => ({ key: k, type: String(v ?? "") }))
        : [];
    console.log(mappingRows);
    return (
        <div style={{ display: "grid", gap: 10 }}>
            <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Supported File Types</div>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{supportedFileMessage}</div>
            </div>
            {thoughts && (
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Analyzer Notes</div>
                    <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{thoughts}</div>
                </div>
            )}

            {originalFilename && (
                <div>
                    <div style={{ fontWeight: 600 }}>Original file</div>
                    <div>{originalFilename}</div>
                </div>
            )}

            {(delimiter || headerDetected !== undefined) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {delimiter && (
                        <div>
                            <div style={{ fontWeight: 600 }}>Detected delimiter</div>
                            <div>{JSON.stringify(delimiter)}</div>
                        </div>
                    )}
                    {headerDetected !== undefined && (
                        <div>
                            <div style={{ fontWeight: 600 }}>Header row detected</div>
                            <div>{headerDetected ? "Yes" : "No"}</div>
                        </div>
                    )}
                </div>
            )}

            {/* NEW: Column headers from mappingJson */}
            {mappingRows.length > 0 && (
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        Detected column headers ({mappingRows.length})
                    </div>
                    <div
                        style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 6,
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr auto",
                                gap: 0,
                                fontWeight: 600,
                                background: "#f7f7f7",
                                padding: "6px 10px",
                                borderBottom: "1px solid #e0e0e0",
                            }}
                        >
                            <div>Header</div>
                            <div>Type</div>
                        </div>
                        <div>
                            {mappingRows.map((row, i) => (
                                <div
                                    key={i}
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr auto",
                                        gap: 0,
                                        padding: "6px 10px",
                                        borderTop: i === 0 ? "none" : "1px solid #eee",
                                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                    }}
                                >
                                    <div style={{ overflowWrap: "anywhere" }}>{row.key}</div>
                                    <div style={{ opacity: 0.8 }}>{row.type}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {sampleLines.length > 0 && (
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        Sample lines (first {sampleLines.length})
                    </div>
                    <pre
                        style={{
                            padding: 10,
                            background: "#f7f7f7",
                            border: "1px solid #e0e0e0",
                            borderRadius: 6,
                            overflowX: "auto",
                            margin: 0,
                        }}
                    >
{sampleLines.join("\n")}
          </pre>
                </div>
            )}
        </div>
    );
}

// Normalizes Kendo refs to DOM nodes and gives stable focusing
export const setDomRef = (reactRef) => (el) => { reactRef.current = el?.element ?? el ?? null; };
export const focusNode = (ref) => {
    const node = ref?.current;
    if (node && typeof node.focus === "function") requestAnimationFrame(() => node.focus());
};
/**
 * removes: nulls, empty strings, empty objects, and empty arrays from obj
 * this is a recursive function if obj[propName] is an object this will
 * @param obj {Object} any object
 * @returns {Object} object with empty values removed
 */
export const stripEmptyValuesFromObject = (obj) => {
    for (let propName in obj) {
        if ($.isPlainObject(obj[propName])) {
            obj[propName] = stripEmptyValuesFromObject(obj[propName])
        }
        if (obj[propName] === null || obj[propName] === '' || $.isEmptyObject(obj[propName]) || ($.isArray(obj[propName]) && !obj[propName].length)) {
            delete obj[propName];
        }
    }
    return obj;
};
const buildPrimaryData = (targetType, audienceDesignFormik) => {
    if (targetType === "BUSINESS") {
        return buildBusinessSegments(audienceDesignFormik);
    } else {
        return buildPeopleSegments(audienceDesignFormik);
    }
};

export const buildPeopleSegments = (audienceDesignFormik) => {
    return {
        age_range: audienceDesignFormik.values.segment_design.primaryData.age_range,
        income_cd: audienceDesignFormik.values.segment_design.primaryData.income_cd,
        gender: audienceDesignFormik.values.segment_design.primaryData.gender,
        education_cd: audienceDesignFormik.values.segment_design.primaryData.education_cd,
        sem_ethnic_cd: audienceDesignFormik.values.segment_design.primaryData.sem_ethnic_cd,
        sem_ethnic_region_cd: audienceDesignFormik.values.segment_design.primaryData.sem_ethnic_region_cd,
        reg_party_cd: audienceDesignFormik.values.segment_design.primaryData.reg_party_cd,
        districts: audienceDesignFormik.values.segment_design.primaryData.districts,
        digital_activity_index: audienceDesignFormik.values.segment_design.primaryData.digital_activity_index.flat(),
        inflation_sensitivity_index: audienceDesignFormik.values.segment_design.primaryData.inflation_sensitivity_index.flat(),
        social_matrix: audienceDesignFormik.values.segment_design.primaryData.social_matrix,
        home_value_cd: audienceDesignFormik.values.segment_design.primaryData.home_value_cd,
        homeowner_flg: audienceDesignFormik.values.segment_design.primaryData.homeowner_flg,
        zip3: audienceDesignFormik.values.segment_design.primaryData.zip3,
        zip5: audienceDesignFormik.values.segment_design.primaryData.zip5,
        state: audienceDesignFormik.values.segment_design.primaryData.state,
        city: audienceDesignFormik.values.segment_design.primaryData.city
    }
};

export const buildBusinessSegments = (audienceDesignFormik) => {
    console.info("buildBusinessSegments", audienceDesignFormik)
    return {
        revenue_cd: audienceDesignFormik.values.segment_design.primaryData.revenue_cd,
        employees_total_cd: audienceDesignFormik.values.segment_design.primaryData.employees_total_cd,
        naics: audienceDesignFormik.values.segment_design.primaryData.naics,
        zip3: audienceDesignFormik.values.segment_design.primaryData.zip3,
        zip5: audienceDesignFormik.values.segment_design.primaryData.zip5,
        state: audienceDesignFormik.values.segment_design.primaryData.state,
        city: audienceDesignFormik.values.segment_design.primaryData.city
    }
};

export const buildSegmentDesign = (audienceDesignFormik) => {
    console.log(audienceDesignFormik)
    const consumer = audienceDesignFormik.values.segment_design.consumer;
    const sportsAttendees = audienceDesignFormik.values.segment_design.sportsAttendees;
    const politicalAffiliations = audienceDesignFormik.values.segment_design.politicalAffiliations;
    const mergeType = audienceDesignFormik.values.segment_design.merge_type;
    const targetType = audienceDesignFormik.values.segment_design.target_type;
    const primaryData = buildPrimaryData(targetType, audienceDesignFormik);
    const homeOwnerData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.homeOwner : [];
    const personalFinanceData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.personalFinance : [];
    const titleMatchingData = targetType === "CONSUMER" || targetType === "BUSINESS" ? audienceDesignFormik.values.segment_design.titleMatching : [];
    const specialtyData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.specialtySegments : [];
    const businessSpecialtyData = targetType === "BUSINESS" ? audienceDesignFormik.values.segment_design.specialtySegmentsBusiness : [];
    const travelData = targetType === "BUSINESS" ? audienceDesignFormik.values.segment_design.travelSegments : [];
    // const socialMatrixData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.social_matrix : [];
    const spectrumMediaData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.spectrumMedia : [];
    // const qwarryData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.qwarry : [];
    const acxiomLifestyleData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.acxiomLifestyle : [];
    const analyticsIQData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.analyticsIQ : [];
    // const affinitySolutionsData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.affinitySolutions : [];
    const hcpspecialtySegmentsData = targetType === "BUSINESS" ? audienceDesignFormik.values.segment_design.hcpspecialtySegmentsBusiness : [];
    // const healthWiseData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.healthWise : [];
    const knowWhoConsumerData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.knowWhoConsumer : [];
    const purpleLabData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.purpleLab : [];
    const purpleLabHCPData = targetType === "CONSUMER" || targetType === "BUSINESS" ? audienceDesignFormik.values.segment_design.purpleLabHCP : [];
    const purpleLabHPDData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.purpleLabHPD : [];
    const l2DataData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.l2Data : [];
    const datatrustData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.datatrust : [];
    const smllearningData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.smllearning : [];
    // FD: 10912
    // const i360Data = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.i360 : [];
    // const throtleData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.throtle : [];
    // const iqviaData = targetType === "CONSUMER" || targetType === "BUSINESS" ? audienceDesignFormik.values.segment_design.iqvia : [];
    const salesintelData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.salesintel : [];
    const salesinteltechnoData = targetType === "CONSUMER" ? audienceDesignFormik.values.segment_design.salesinteltechno : [];



    let fileData = {
        consumer: consumer,
        politicalAffiliations: politicalAffiliations,
        sportsAttendees: sportsAttendees,
        personalFinance: personalFinanceData,
        homeOwner: homeOwnerData,
        titleMatching: titleMatchingData,
        specialtySegments: specialtyData,
        specialtySegmentsBusiness: businessSpecialtyData,
        travelSegments: travelData,
        merge_type: mergeType,
        target_type: targetType,
        primaryData: primaryData,
        acxiomLifestyle: acxiomLifestyleData,
        analyticsIQ: analyticsIQData,
        hcpspecialtySegmentsBusiness: hcpspecialtySegmentsData,
        // healthWise:healthWiseData,
        // FD: 10912
        // i360:i360Data,
        // iqvia:iqviaData,
        knowWhoConsumer:knowWhoConsumerData,
        l2Data:l2DataData,
        datatrust:datatrustData,
        smllearning:smllearningData,
        purpleLab:purpleLabData,
        purpleLabHCP:purpleLabHCPData,
        purpleLabHPD:purpleLabHPDData,
        salesintel:salesintelData,
        salesinteltechno:salesinteltechnoData,
        spectrumMedia: spectrumMediaData,
        // qwarry: qwarryData,
        // throtle:throtleData,
    }
    return stripEmptyValuesFromObject(fileData);
};

export const EXPORT_TYPE_OPTIONS = [
    { id: "FILE_EXCHANGE", name: "File Exchange" },
    { id: "FOLDER_EXCHANGE", name: "Folder Exchange" },
    { id: "HOUSEHOLD_EXPORT", name: "Household Export" },
    { id: "BUSINESS_EXPORT",  name: "Business Export" },
    { id: "PERSON_EXPORT",    name: "Person Export" },
];

export function processPairData(input) {
    let result = [];
    let pairs = {
        "01": "02",
        "03": "04",
        "05": "06",
        "07": "08",
        "09": "10"
    };

    for (let i = 0; i < input.length; i++) {
        if (input[i] === "00") {
            result.push(["00"]);
        } else if (pairs[input[i]] && input[i + 1] === pairs[input[i]]) {
            result.push([input[i], input[i + 1]]);
            i++; // Skip the next item since it's already paired
        } else {
            result.push([input[i]]);
        }
    }
    return result;
}
export function InitialAudienceState(res) {
    //segment data are set to empty for edit mode only, for FD#15079
    return {
        idxContain: res.data.idxContain ?res.data.idxContain : [],
        group_id: res.data.group_id,
        // segment_name: res.data.segment_name,
        // originalSegmentName: res.data.segment_name,
        target_type: res.data.segment_design.target_type,
        original_target_type: res.data.segment_design.target_type,
        notes: res.data.notes ? res.data.notes : "",
        // keywords: res.data.segment_design.keywords ? res.data.segment_design.keywords : "",
        // sites: res.data.segment_design.sites ? res.data.segment_design.sites : "",
        exclude_segment_id: res.data.segment_design.exclude_segment_id ? res.data.segment_design.exclude_segment_id:[],
        age_range: res.data.segment_design.primaryData && res.data.segment_design.primaryData.age_range ? res.data.segment_design.primaryData.age_range : [],
        city: res.data.segment_design.primaryData && res.data.segment_design.primaryData.city ? res.data.segment_design.primaryData.city : [],
        digital_activity_index: res.data.segment_design.primaryData && res.data.segment_design.primaryData.digital_activity_index ? processPairData(res.data.segment_design.primaryData.digital_activity_index) : [],
        districts: res.data.segment_design.primaryData && res.data.segment_design.primaryData.districts ? res.data.segment_design.primaryData.districts : [],
        employees_total_cd: res.data.segment_design.primaryData && res.data.segment_design.primaryData.employees_total_cd ? res.data.segment_design.primaryData.employees_total_cd : [],
        first_party_location_focus: res.data.first_party_location_focus ? res.data.first_party_location_focus : "",
        gender: res.data.segment_design.primaryData && res.data.segment_design.primaryData.gender ? res.data.segment_design.primaryData.gender : [],
        education_cd: res.data.segment_design.primaryData && res.data.segment_design.primaryData.education_cd ? res.data.segment_design.primaryData.education_cd : [],
        home_value_cd: res.data.segment_design.primaryData && res.data.segment_design.primaryData.home_value_cd ? res.data.segment_design.primaryData.home_value_cd : [],
        homeowner_flg: res.data.segment_design.primaryData && res.data.segment_design.primaryData.homeowner_flg ? res.data.segment_design.primaryData.homeowner_flg :[],
        income_cd: res.data.segment_design.primaryData && res.data.segment_design.primaryData.income_cd ? res.data.segment_design.primaryData.income_cd : [],
        inflation_sensitivity_index: res.data.segment_design.primaryData && res.data.segment_design.primaryData.inflation_sensitivity_index ? processPairData(res.data.segment_design.primaryData.inflation_sensitivity_index) : [],
        naics: res.data.segment_design.primaryData && res.data.segment_design.primaryData.naics ? res.data.segment_design.primaryData.naics : [],
        reg_party_cd: res.data.segment_design.primaryData && res.data.segment_design.primaryData.reg_party_cd ? res.data.segment_design.primaryData.reg_party_cd : [],
        revenue_cd: res.data.segment_design.primaryData && res.data.segment_design.primaryData.revenue_cd ? res.data.segment_design.primaryData.revenue_cd : [],
        sem_ethnic_cd: res.data.segment_design.primaryData && res.data.segment_design.primaryData.sem_ethnic_cd ? res.data.segment_design.primaryData.sem_ethnic_cd : [],
        sem_ethnic_region_cd: res.data.segment_design.primaryData && res.data.segment_design.primaryData.sem_ethnic_region_cd ? res.data.segment_design.primaryData.sem_ethnic_region_cd : [],
        social_matrix: res.data.segment_design.primaryData && res.data.segment_design.primaryData.social_matrix ? res.data.segment_design.primaryData.social_matrix : [],
        state: res.data.segment_design.primaryData && res.data.segment_design.primaryData.state ? res.data.segment_design.primaryData.state : [],
        zip3: res.data.segment_design.primaryData && res.data.segment_design.primaryData.zip3 ? res.data.segment_design.primaryData.zip3 : [],
        zip5: res.data.segment_design.primaryData && res.data.segment_design.primaryData.zip5 ? res.data.segment_design.primaryData.zip5 : [],
        acxiomLifestyle: [],
        analyticsIQ: [],
        consumer: [],//retail
        hcpspecialtySegmentsBusiness: [],
        homeOwner: [],
        knowWhoConsumer: [],
        l2Data: [],
        datatrust: [],
        smllearning:  [],
        merge_type: res.data.segment_design.merge_type ? res.data.segment_design.merge_type : "",
        personalFinance: [],
        politicalAffiliations: [],
        purpleLab: [],
        purpleLabHCP: [],
        purpleLabHPD: [],
        salesintel: [],
        salesinteltechno: [],
        specialtySegments: [],
        specialtySegmentsBusiness: [],
        spectrumMedia:  [],
        sportsAttendees: [],
        titleMatching: [],
        segment_date_start: res.data.segment_design.primaryData && res.data.segment_design.primaryData.segment_date_start ? res.data.segment_design.primaryData.segment_date_start : "",
        segment_date_end: res.data.segment_design.primaryData && res.data.segment_design.primaryData.segment_date_end ? res.data.segment_design.primaryData.segment_date_end : "",
        travelSegments: [],
    };
}
